import { AnimatePresence, motion } from 'framer-motion';
import { Component, useEffect, useState, type ErrorInfo, type ReactNode } from 'react';
import { BrandReveal, NeevCloudMark } from './components/Brand';
import { ErrorState, Spinner, ThemeToggle } from './components/primitives';
import { ToastProvider } from './components/Toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { DashboardPage } from './pages/DashboardPage';
import { LoginPage } from './pages/LoginPage';

/* -------------------------------------------------------------------------- */
/* Error boundary — a render crash should not leave a blank page               */
/* -------------------------------------------------------------------------- */

class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[render error]', error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-dvh items-center justify-center p-6">
          <div className="w-full max-w-lg">
            <ErrorState
              title="This page hit an unexpected error"
              message={this.state.error.message || 'An unknown error occurred while rendering.'}
              onRetry={() => window.location.reload()}
            />
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/* -------------------------------------------------------------------------- */
/* Brand transition — the moment between login and dashboard                  */
/* -------------------------------------------------------------------------- */

function BrandTransition({ onDone }: { onDone: () => void }) {
  useEffect(() => {
    // Short and single-stage on purpose — a fade + scale of the mark, not a
    // performance. Long enough to register, short enough to never feel like
    // something standing between the user and their work.
    const id = window.setTimeout(onDone, 950);
    return () => window.clearTimeout(id);
  }, [onDone]);

  return (
    <motion.div
      initial={{ opacity: 1 }}
      exit={{ opacity: 0, scale: 1.06 }}
      transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
      className="fixed inset-0 z-[200] flex flex-col items-center justify-center bg-ink-950"
      aria-hidden="true"
    >
      <div className="absolute inset-0 bg-[radial-gradient(40rem_32rem_at_50%_45%,rgb(139_92_246/0.14),transparent_70%)]" />
      <BrandReveal size={140} />
      <motion.p
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35, duration: 0.4 }}
        className="mt-7 text-xs font-semibold tracking-[0.32em] text-lotus-300 uppercase"
      >
        Welcome back
      </motion.p>
    </motion.div>
  );
}

/* -------------------------------------------------------------------------- */

function Shell() {
  const { user, loading, bootError, justAuthenticated, clearJustAuthenticated, retryBoot } =
    useAuth();
  const [introDone, setIntroDone] = useState(false);

  // Reset the intro gate whenever a new session starts.
  useEffect(() => {
    if (justAuthenticated) setIntroDone(false);
  }, [justAuthenticated]);

  if (loading) {
    return (
      <div className="relative flex min-h-dvh flex-col items-center justify-center gap-5">
        <ThemeToggle className="absolute top-4 right-4" />
        <NeevCloudMark size={64} />
        <div className="flex items-center gap-2 text-sm text-mist-500">
          <Spinner className="h-4 w-4" />
          Preparing your board…
        </div>
      </div>
    );
  }

  if (bootError) {
    return (
      <div className="relative flex min-h-dvh items-center justify-center p-6">
        <ThemeToggle className="absolute top-4 right-4" />
        <div className="w-full max-w-lg">
          <ErrorState
            title="Cannot reach the server"
            message={`${bootError} Make sure the API is running on http://localhost:4000 — see the README for the start command.`}
            onRetry={retryBoot}
          />
        </div>
      </div>
    );
  }

  const showIntro = Boolean(user) && justAuthenticated && !introDone;

  return (
    <>
      {user ? <DashboardPage /> : <LoginPage />}
      <AnimatePresence
        onExitComplete={() => {
          clearJustAuthenticated();
        }}
      >
        {showIntro && <BrandTransition key="brand" onDone={() => setIntroDone(true)} />}
      </AnimatePresence>
    </>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <ErrorBoundary>
        <ToastProvider>
          <AuthProvider>
            <Shell />
          </AuthProvider>
        </ToastProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
}
